/**
 * Plant & Equipment API client — Session 3.15 (Section 9).
 *
 * Mirrors the public surfaces exported by
 * apps/api/src/services/plantEquipment/plantEquipmentService.ts. All routes
 * except /overdue-count require Plus tier or above; Essentials tenants
 * receive an ApiError with code 'TIER_REQUIRED' which callers should treat
 * as a non-fatal "tier gate" state.
 */

import { apiFetch, apiDownload, apiUrl } from './api';
import { getAccessToken } from './auth';
import type { RegisterPrint, RegisterPrintLog } from '../components/RegisterPrintHistory';
import { companyTodayIso } from './companyClock';

/* ─────────────────────────── Public types ─────────────────────────── */

/** The three equipment registers (2026-08-20, ASSETS-MAINTENANCE-001). */
export type EquipmentRegister = 'plant' | 'tools' | 'fleet';

export interface EquipmentItemPublic {
  id: string;
  tenantId: string;
  /** Multi-site Phase 2b — every equipment item is anchored to a Site. */
  siteId: string;
  /** Which register the item belongs to. */
  register: EquipmentRegister;
  environment: string;
  equipmentType: string;
  name: string;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  yearOfManufacture: number | null;
  locationDetail: string | null;
  status: string;
  purchaseDate: string | null;
  purchasePrice: string | null;
  supplierName: string | null;
  insuranceRef: string | null;
  insuredValue: string | null;
  nextInspectionDue: string | null;
  requiredQualifications: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EquipmentStats {
  total: number;
  inService: number;
  inspectionDue: number;
  quarantined: number;
}

export interface EquipmentListResult {
  equipment: EquipmentItemPublic[];
  stats: EquipmentStats;
}

export interface InspectionPublic {
  id: string;
  tenantId: string;
  equipmentId: string;
  inspectionType: string;
  scheduledDate: string | null;
  completedDate: string | null;
  status: string;
  inspectedById: string | null;
  inspectorCompany: string | null;
  inspectorName: string | null;
  inspectorQualification: string | null;
  outcome: string | null;
  defectsFound: string[];
  defectsRectified: boolean;
  rectificationDate: string | null;
  nextInspectionDate: string | null;
  reportKey: string | null;
  certificateRef: string | null;
  notes: string | null;
  createdAt: string;
  /**
   * The certificate FILE filed for this inspection in the Document Register,
   * and the version to open. Null when none is filed (certificateRef is then
   * just the reference written down). On the history reads only.
   */
  certificateDocumentId?: string | null;
  certificateVersionId?: string | null;
}

export interface InspectionScheduleResult {
  overdue: InspectionPublic[];
  dueSoon: InspectionPublic[];
  upcoming: InspectionPublic[];
}

export interface EquipmentDetailResult {
  equipment: EquipmentItemPublic;
  inspections: InspectionPublic[];
  upcomingInspections: InspectionPublic[];
  lastPreUse: InspectionPublic | null;
}

export interface PreUseCheckTodayRow {
  equipment: EquipmentItemPublic;
  latestPreUse: InspectionPublic | null;
}

export interface ImportPreviewRow {
  valid: boolean;
  errors: string[];
  row: Record<string, string>;
}

export interface UploadImportResult {
  batchId: string;
  preview: ImportPreviewRow[];
}

export interface ConfirmImportResult {
  insertedCount: number;
}

/* ─────────────────────────── Input payloads ─────────────────────────── */

export interface CreateEquipmentPayload {
  /** Multi-site Phase 2b — required. */
  siteId: string;
  /** Which register to file the item under — 'plant' (default) | 'tools' | 'fleet'. */
  register?: 'plant' | 'tools' | 'fleet';
  environment: string;
  equipmentType: string;
  name: string;
  make?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  assetTag?: string | null;
  yearOfManufacture?: number | null;
  locationDetail?: string | null;
  status?: 'in_service' | 'quarantined' | 'under_repair' | 'decommissioned';
  purchaseDate?: string | null;
  purchasePrice?: string | null;
  supplierName?: string | null;
  insuranceRef?: string | null;
  insuredValue?: string | null;
  nextInspectionDue?: string | null;
  requiredQualifications?: string[];
  notes?: string | null;
}

export type UpdateEquipmentPayload = Partial<CreateEquipmentPayload>;

export interface RecordInspectionPayload {
  equipmentId: string;
  inspectionType:
    | 'thorough_examination'
    | 'routine_inspection'
    | 'written_scheme_exam'
    | 'lev_test'
    | 'pre_use'
    | 'post_incident';
  scheduledDate?: string | null;
  completedDate?: string | null;
  status?: 'scheduled' | 'complete' | 'overdue' | 'failed';
  inspectedById?: string | null;
  inspectorCompany?: string | null;
  inspectorName?: string | null;
  inspectorQualification?: string | null;
  outcome?: 'passed' | 'passed_with_defects' | 'failed' | null;
  defectsFound?: string[];
  defectsRectified?: boolean;
  rectificationDate?: string | null;
  nextInspectionDate?: string | null;
  reportKey?: string | null;
  certificateRef?: string | null;
  notes?: string | null;
}

export interface SubmitPreUseCheckPayload {
  equipmentId: string;
  items: { name: string; checked: boolean }[];
  notes?: string | null;
}

export interface ListEquipmentQuery {
  environment?: string;
  status?: string;
  search?: string;
  /** Multi-site Phase 2b — when set, scopes the list to one site. */
  siteId?: string;
  /** Building vs Fleet register — when set, scopes to that register. */
  register?: 'plant' | 'tools' | 'fleet';
}

/* ─────────────────────────── Equipment CRUD ─────────────────────────── */

export async function fetchEquipment(
  q: ListEquipmentQuery = {},
  signal?: AbortSignal,
): Promise<EquipmentListResult> {
  return apiFetch<EquipmentListResult>('/api/plant/equipment', {
    method: 'GET',
    query: {
      environment: q.environment ?? null,
      status: q.status ?? null,
      search: q.search ?? null,
      // Multi-site Phase 2b — when set, scopes the list to one site.
      siteId: q.siteId ?? null,
      // Building vs Fleet register scoping.
      register: q.register ?? null,
    },
    signal,
  });
}

export async function fetchEquipmentById(
  id: string,
  signal?: AbortSignal,
): Promise<EquipmentDetailResult> {
  return apiFetch<EquipmentDetailResult>(`/api/plant/equipment/${encodeURIComponent(id)}`, {
    method: 'GET',
    signal,
  });
}

export async function createEquipment(
  payload: CreateEquipmentPayload,
): Promise<EquipmentItemPublic> {
  return apiFetch<EquipmentItemPublic>('/api/plant/equipment', {
    method: 'POST',
    body: payload,
  });
}

export async function updateEquipment(
  id: string,
  payload: UpdateEquipmentPayload,
): Promise<EquipmentItemPublic> {
  return apiFetch<EquipmentItemPublic>(`/api/plant/equipment/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function updateEquipmentStatus(
  id: string,
  newStatus: 'in_service' | 'quarantined' | 'under_repair' | 'decommissioned',
): Promise<EquipmentItemPublic> {
  return apiFetch<EquipmentItemPublic>(
    `/api/plant/equipment/${encodeURIComponent(id)}/status`,
    { method: 'PUT', body: { status: newStatus } },
  );
}

export async function deleteEquipment(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/plant/equipment/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** Remove a calibration instrument. Soft delete - certificates and history stay. */
export async function deleteInstrument(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/plant/calibration/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** An instrument taken out of service: off the live register, kept as a record. */
export interface OutOfServiceInstrument {
  id: string;
  name: string;
  category: CalibrationCategory;
  serialRef: string;
  location: string;
  lastCalibratedDate: string | null;
  lastOutcome: 'pass' | 'pass_with_advisory' | 'fail' | null;
  outOfServiceAt: string;
  outOfServiceBy: string | null;
  reason: string;
}

export async function fetchOutOfServiceInstruments(signal?: AbortSignal): Promise<OutOfServiceInstrument[]> {
  return apiFetch<OutOfServiceInstrument[]>('/api/plant/calibration/out-of-service', { method: 'GET', signal });
}

export async function takeInstrumentOutOfService(id: string, reason: string): Promise<void> {
  await apiFetch<unknown>(`/api/plant/calibration/${encodeURIComponent(id)}/out-of-service`, {
    method: 'POST',
    body: { reason },
  });
}

export async function returnInstrumentToService(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/plant/calibration/${encodeURIComponent(id)}/return-to-service`, {
    method: 'POST',
  });
}

/* ─────────────────────────── Inspections ─────────────────────────── */

export async function fetchInspectionSchedule(
  signal?: AbortSignal,
): Promise<InspectionScheduleResult> {
  return apiFetch<InspectionScheduleResult>('/api/plant/inspections', {
    method: 'GET',
    signal,
  });
}

export async function fetchInspectionsForEquipment(
  equipmentId: string,
  signal?: AbortSignal,
): Promise<InspectionPublic[]> {
  return apiFetch<InspectionPublic[]>(
    `/api/plant/inspections/${encodeURIComponent(equipmentId)}`,
    { method: 'GET', signal },
  );
}

export async function recordInspection(
  payload: RecordInspectionPayload,
): Promise<InspectionPublic> {
  return apiFetch<InspectionPublic>('/api/plant/inspections', {
    method: 'POST',
    body: payload,
  });
}

/* ─────────────────────────── Pre-use checks ─────────────────────────── */

export async function fetchPreUseChecksToday(
  signal?: AbortSignal,
): Promise<PreUseCheckTodayRow[]> {
  return apiFetch<PreUseCheckTodayRow[]>('/api/plant/pre-use/today', {
    method: 'GET',
    signal,
  });
}

export async function submitPreUseCheck(
  payload: SubmitPreUseCheckPayload,
): Promise<InspectionPublic> {
  return apiFetch<InspectionPublic>('/api/plant/pre-use', {
    method: 'POST',
    body: payload,
  });
}

/* ─────────────────────────── Import / Export ─────────────────────────── */

/**
 * Download the asset-register import template.
 *
 * Added 2026-08-16 — the page used a bare anchor which carried no Bearer
 * token and answered 401. IMPORT-EXPORT-AUDIT-001 FAULT 5.
 */
export async function downloadPlantImportTemplate(): Promise<void> {
  await apiDownload('/api/plant/import-template', 'plant_equipment_import.csv');
}

/**
 * Export endpoint returns text/csv. Raw fetch so we can read the response
 * body as text rather than unwrapping the JSON envelope.
 */
export async function exportEquipmentCsv(register?: 'plant' | 'tools' | 'fleet'): Promise<string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const url = register ? `/api/plant/export?register=${register}` : '/api/plant/export';
  const res = await fetch(apiUrl(url), { method: 'GET', headers });
  if (!res.ok) {
    throw new Error('Export failed');
  }
  return res.text();
}

/* ─────────────────────────── Tenant option deltas ─────────────────────────── */

/** One tenant DELTA over the built-in Environment / Equipment type lists
 *  (Shane, 2026-07-24: "nothing is fixed code"). A row is a rename of a
 *  built-in (value = the built-in's value, new label), a custom addition
 *  (value === label), or a removal of a built-in (hidden === true). The
 *  effective list is resolved from the built-ins + these deltas. */
export type PlantOptionKind = 'environment' | 'equipment_type';

export interface PlantOptionDelta {
  kind: string;
  value: string;
  label: string;
  hidden: boolean;
}

export async function fetchPlantOptionDeltas(signal?: AbortSignal): Promise<PlantOptionDelta[]> {
  const data = await apiFetch<{ deltas: PlantOptionDelta[] }>('/api/plant/options', {
    method: 'GET',
    signal,
  });
  return data.deltas;
}

/** Upsert a delta — rename (label), add custom (value === label), or remove a
 *  built-in (hidden: true). */
export async function upsertPlantOptionDelta(input: {
  kind: PlantOptionKind;
  value: string;
  label: string;
  hidden?: boolean;
}): Promise<PlantOptionDelta> {
  return apiFetch<PlantOptionDelta>('/api/plant/options', { method: 'PUT', body: input });
}

/** Delete a delta — reset a built-in to default, or drop a custom addition. */
export async function deletePlantOptionDelta(
  kind: PlantOptionKind,
  value: string,
): Promise<void> {
  await apiFetch<{ deleted: boolean }>(
    `/api/plant/options?kind=${kind}&value=${encodeURIComponent(value)}`,
    { method: 'DELETE' },
  );
}

export async function uploadImportCsv(csv: string): Promise<UploadImportResult> {
  return apiFetch<UploadImportResult>('/api/plant/import/upload', {
    method: 'POST',
    body: { csv },
  });
}

export async function confirmImport(batchId: string): Promise<ConfirmImportResult> {
  return apiFetch<ConfirmImportResult>(
    `/api/plant/import/confirm/${encodeURIComponent(batchId)}`,
    { method: 'POST' },
  );
}

/* ─────────────────────────── Sidebar badge ─────────────────────────── */

export async function fetchOverdueCount(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/plant/overdue-count', {
    method: 'GET',
    signal,
  });
  return data.count;
}

/* ── Register documents — PDF and Excel (2026-08-16) ────────────────────────
 *
 * Four buttons used to `window.alert('Exporting … as PDF…')` and produce no
 * file at all; "Export as Excel" called the CSV handler and handed over a
 * .csv. IMPORT-EXPORT-AUDIT-001 FAULTS 2 and 6. These fetch the real thing.
 */

export type PlantExportKind =
  | 'register'
  | 'tools_register'
  | 'fleet_register'
  | 'loler'
  | 'inspections';

const KIND_FILENAME: Record<PlantExportKind, string> = {
  register: 'plant-equipment-register',
  tools_register: 'tools-equipment-register',
  fleet_register: 'fleet-equipment-register',
  loler: 'loler-register',
  inspections: 'inspection-history',
};

export async function downloadPlantPdf(kind: PlantExportKind): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/plant/export.pdf', `${KIND_FILENAME[kind]}-${stamp}.pdf`, {
    query: { kind },
  });
}

export async function downloadPlantXlsx(kind: PlantExportKind): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/plant/export.xlsx', `${KIND_FILENAME[kind]}-${stamp}.xlsx`, {
    query: { kind },
  });
}

/* ------------------------------------------------------------------ */
/*  Calibration Records                                                */
/*                                                                     */
/*  Moved here from buildingMaintenanceApi on 2026-08-18 (Shane: the   */
/*  instruments are equipment, not building fabric). The endpoints     */
/*  moved with it — /api/plant/calibration, not building-maintenance.  */
/* ------------------------------------------------------------------ */

export type CalibrationCategory =
  | 'torque'
  | 'electrical'
  | 'pressure'
  | 'temperature'
  | 'dimensional'
  | 'flow'
  | 'other';

/** out_of_use: the last calibration failed; it stays out until a later one passes. */
export type CalibrationStatus = 'current' | 'due_soon' | 'overdue' | 'out_of_use';

export interface CalibrationInstrumentPublic {
  id: string;
  tenantId: string;
  siteId: string | null;
  name: string;
  category: CalibrationCategory;
  serialRef: string;
  location: string;
  frequencyMonths: number | null;
  lastCalibratedDate: string | null;
  nextDueDate: string | null;
  contractorId: string | null;
  contractorName: string | null;
  completedById: string | null;
  certificateS3Key: string | null;
  /** The latest calibration's filed certificate, as the attachment link it is
   *  opened and downloaded by. Null when that calibration has none logged. */
  certificateLinkId: string | null;
  certificateFilename: string | null;
  status: CalibrationStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalibrationStats {
  total: number;
  current: number;
  dueSoon: number;
  overdue: number;
  outOfUse: number;
}

export interface CalibrationListResult {
  instruments: CalibrationInstrumentPublic[];
  stats: CalibrationStats;
}

export interface CreateInstrumentPayload {
  name: string;
  category: CalibrationCategory;
  serialRef: string;
  location: string;
  siteId?: string | null;
  frequencyMonths?: number | null;
  lastCalibratedDate?: string | null;
  nextDueDate?: string | null;
  contractorId?: string | null;
  contractorName?: string | null;
  completedById?: string | null;
  certificateS3Key?: string | null;
  notes?: string | null;
}

export interface UpdateInstrumentPayload {
  name?: string;
  category?: CalibrationCategory;
  serialRef?: string;
  location?: string;
  siteId?: string | null;
  frequencyMonths?: number | null;
  lastCalibratedDate?: string | null;
  nextDueDate?: string | null;
  contractorId?: string | null;
  contractorName?: string | null;
  completedById?: string | null;
  certificateS3Key?: string | null;
  notes?: string | null;
}

/**
 * The Calibration Register as a controlled document (2026-08-30).
 *
 * Shane: *"if we print it needs to allocate a version and dated so someone can
 * see that that print was 3 yrs ago and is out of date"*. The register on
 * screen is always current, so it needs no version; the PRINT is the copy that
 * goes stale, and it carries the stamp and gets logged.
 */
export type CalibrationRegisterPrint = RegisterPrint;
export type CalibrationPrintLog = RegisterPrintLog;

/**
 * Downloads the stamped PDF. Not window.print() — a browser print of the live
 * page carries the sidebar, no stamp, and leaves no record that a copy exists.
 */
export async function downloadCalibrationRegisterPdf(): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/plant/calibration/register.pdf', `calibration-register-${stamp}.pdf`);
}

export async function fetchCalibrationPrintLog(signal?: AbortSignal): Promise<CalibrationPrintLog> {
  return apiFetch<CalibrationPrintLog>('/api/plant/calibration/print-log', {
    method: 'GET',
    signal,
  });
}

/**
 * The two Inspections-tab documents (2026-08-30). Shane, having enumerated the
 * section himself: *"have you checked them all"* - three tables live on that
 * tab and only LOLER could be printed. Then: *"yes add them all"*.
 */
export async function downloadInspectionSchedulePdf(): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/plant/inspections/schedule.pdf', `inspection-schedule-${stamp}.pdf`);
}

export async function downloadPreUseChecksPdf(): Promise<void> {
  const stamp = companyTodayIso();
  await apiDownload('/api/plant/pre-use/today.pdf', `pre-use-checks-${stamp}.pdf`);
}

/**
 * Who has printed a plant register. `kind` picks which - the same values as the
 * PDF download, plus `inspection_schedule` and `pre_use` for the two documents
 * that have no export.pdf of their own.
 */
export async function fetchPlantPrintLog(
  kind: PlantExportKind | 'inspection_schedule' | 'pre_use',
  signal?: AbortSignal,
): Promise<RegisterPrintLog> {
  return apiFetch<RegisterPrintLog>('/api/plant/print-log', {
    method: 'GET',
    query: { kind },
    signal,
  });
}

export async function fetchCalibration(
  opts: { status?: CalibrationStatus | ''; search?: string } = {},
  signal?: AbortSignal,
): Promise<CalibrationListResult> {
  return apiFetch<CalibrationListResult>('/api/plant/calibration', {
    method: 'GET',
    query: {
      status: opts.status || null,
      search: opts.search || null,
    },
    signal,
  });
}

export async function createInstrument(
  payload: CreateInstrumentPayload,
): Promise<CalibrationInstrumentPublic> {
  return apiFetch<CalibrationInstrumentPublic>('/api/plant/calibration', {
    method: 'POST',
    body: payload,
  });
}

export async function updateInstrument(
  id: string,
  payload: UpdateInstrumentPayload,
): Promise<void> {
  await apiFetch<unknown>(`/api/plant/calibration/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function logCalibrationRecord(
  id: string,
  payload: {
    calibratedDate: string;
    nextDueDate: string | null;
    contractorId?: string | null;
    contractorName?: string | null;
    completedById?: string | null;
    outcome: 'pass' | 'pass_with_advisory' | 'fail';
    certificateS3Key?: string | null;
    notes?: string | null;
  },
): Promise<{ id: string }> {
  // The record's id comes back so its certificate can be uploaded against it.
  return apiFetch<{ id: string }>(`/api/plant/calibration/${id}/records`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * Log the certificate the calibration company issued against a calibration
 * record. The file is uploaded first with `uploadAttachment` (entityType
 * 'calibration_record', the record's id); this names the link it returned.
 * Files ISO 9001 clause 7.1.5 on the server, once per record.
 */
export async function attachCalibrationCertificate(
  recordId: string,
  attachmentLinkId: string,
): Promise<{ recordId: string; certificateLinkId: string; evidenceFiled: boolean }> {
  return apiFetch<{ recordId: string; certificateLinkId: string; evidenceFiled: boolean }>(
    `/api/plant/calibration/records/${recordId}/certificate`,
    { method: 'POST', body: { attachmentLinkId } },
  );
}

/**
 * The central RAMS templates this tenant does not hold, and taking one.
 *
 * What the Document Register's "+ Add document" picker offers under RAMS.
 * Shane, 2026-08-31: *"they are not showing in the list to add to the tennent..
 * as they are a controlled document... that once they are selected they then go
 * into the RAMS Template libraby for the tennent and can be called up to the
 * SHOW TEMPLATE LIBRARY"*.
 */
export interface AvailableRamsTemplate {
  id: string;
  name: string;
  /** The kind of work it covers - install, delivery, test. */
  category: string;
  /** The filing category: a key on the country's category list. */
  contentCategory: string;
  /** Its name on that list - what a person reads. */
  contentCategoryLabel: string;
  icon: string;
  shortDescription: string;
}

export async function fetchAvailableRamsTemplates(
  signal?: AbortSignal,
): Promise<AvailableRamsTemplate[]> {
  const data = await apiFetch<{ documents: AvailableRamsTemplate[] }>(
    '/api/rams-templates/available',
    { method: 'GET', signal },
  );
  return data.documents;
}

export async function adoptRamsTemplate(
  id: string,
): Promise<{ id: string; name: string; documentNumber: string | null }> {
  const data = await apiFetch<{
    template: { id: string; name: string; documentNumber: string | null };
  }>(`/api/rams-templates/available/${encodeURIComponent(id)}/adopt`, { method: 'POST' });
  return data.template;
}
